/**
 * Test Component for Multi-Model Data Hooks
 *
 * Usage:
 * 1. Import and render this component temporarily in your app
 * 2. Or use the console functions exposed on window.testMultiModelData
 *
 * Example console usage:
 *   window.testMultiModelData.logStatus()
 *   window.testMultiModelData.refetchAll()
 */
import type { ModelCacheDebug } from "@/types/DownloadManager/modelDownloader";
import { clearAllData, getStorageStats } from "@/types/storage/modelDataStorage";
import { useMultiScenarioNodeData } from "@api/models";
import useAppStore from "@hooks/useAppStore";
import useSelectionManager from "@hooks/useSelectionManager";
import { useQueryClient } from "@tanstack/react-query";
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

// MUI Components
import {
  CheckCircle as CheckCircleIcon,
  Delete as ClearOneIcon,
  Close as CloseIcon,
  DeleteForever as DeleteIcon,
  CloudDownload as DownloadIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassIcon,
  Info as InfoIcon,
  ListAlt as ListAltIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

// Expose test utilities on window for console access
declare global {
  interface Window {
    testMultiModelData?: {
      logStatus: () => void;
      refetchAll: () => Promise<void>;
      clearIdbCache: () => Promise<void>;
    };
  }
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`debug-tabpanel-${index}`} aria-labelledby={`debug-tab-${index}`} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `debug-tab-${index}`,
    "aria-controls": `debug-tabpanel-${index}`,
  };
}

// Interface for cached model info
interface CachedModelInfo {
  modelId: string;
  size: number;
  sizeMB: string;
  downloadedAt: string | null;
}

interface MultiModelDataTestProps {
  /** Whether to show the UI panel (default: true) */
  showPanel?: boolean;
  /** Callback when close button is clicked */
  onClose?: () => void;
}

export const MultiModelDataTest = ({ showPanel = true, onClose }: MultiModelDataTestProps) => {
  const [tabValue, setTabValue] = useState(0);
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const scenarioId = selectionManager.selectedScenarioId;
  const selectedUrns = scenarioId ? selectionManager.getSelectedModelUrns(scenarioId) : [];

  // Get selected model IDs - use same method as MuiAssignTab for React Query cache sharing
  // MobX observer() handles reactivity when selections change
  const selectedModelIds = useMemo(() => {
    const models = selectionManager.selectedProject?.models || [];
    return selectedUrns.map((urn: string) => models.find((m: any) => m.apsViewId === urn)?.modelId).filter((id): id is string => !!id);
  }, [selectedUrns, selectionManager.selectedProject?.models]);

  const queryClient = useQueryClient();

  // Unified hook: fetches nodes, materials and supplementals in a single API call
  // Note: MuiAssignTab also uses useMultiScenarioNodeData - they share React Query cache
  const scenarioNodeData = useMultiScenarioNodeData(selectedModelIds, scenarioId, {
    enabled: showPanel && !!scenarioId && selectedModelIds.length > 0,
  });

  // Log status to console
  const logStatus = useCallback(() => {
    console.group("Multi-Model Data Status");

    console.log("Configuration:", {
      scenarioId,
      selectedModelIds,
      modelCount: selectedModelIds.length,
    });

    console.group("Scenario Node Data (Nodes + Materials + Supplementals)");
    console.log("Status:", {
      isLoading: scenarioNodeData.isLoading,
      isFetching: scenarioNodeData.isFetching,
      isSuccess: scenarioNodeData.isSuccess,
      errorCount: scenarioNodeData.errors.filter(Boolean).length,
    });

    // Nodes by Model
    console.log("Nodes by Model:", scenarioNodeData.nodes);
    let totalNodes = 0;
    Object.entries(scenarioNodeData.nodes).forEach(([modelId, data]) => {
      const count = data?.length ?? 0;
      totalNodes += count;
      console.log(`  Model ${modelId}: ${count} nodes`);
    });
    console.log(`  TOTAL: ${totalNodes} nodes`);

    // Materials by Model
    console.log("NodeMaterials by Model:", scenarioNodeData.nodeMaterials);
    let totalMaterials = 0;
    Object.entries(scenarioNodeData.nodeMaterials).forEach(([modelId, data]) => {
      const count = data?.length ?? 0;
      totalMaterials += count;
      console.log(`  Model ${modelId}...: ${count} materials`);
    });
    console.log(`  TOTAL: ${totalMaterials} materials`);

    // Supplementals by Model
    console.log("NodeSupplementals by Model:", scenarioNodeData.nodeSupplementals);
    let totalSupplementals = 0;
    Object.entries(scenarioNodeData.nodeSupplementals).forEach(([modelId, data]) => {
      const count = data?.length ?? 0;
      totalSupplementals += count;
      console.log(`  Model ${modelId}...: ${count} supplementals`);
    });
    console.log(`  TOTAL: ${totalSupplementals} supplementals`);

    console.groupEnd();

    console.groupEnd();
  }, [scenarioId, selectedModelIds, scenarioNodeData]);

  // Refetch functions
  const refetchAll = useCallback(async () => {
    console.log("Refetching all multi-model data...");
    await scenarioNodeData.refetchAll();
    console.log("Refetch complete");
    logStatus();
  }, [scenarioNodeData, logStatus]);

  // Clear IndexedDB cache AND React Query cache
  const clearIdbCache = useCallback(async () => {
    console.log("Clearing IndexedDB and React Query caches...");
    await clearAllData();
    // Reset React Query cache to clear data immediately (not just mark stale)
    // resetQueries clears cached data AND marks as stale, so counts update immediately
    queryClient.resetQueries({ queryKey: ["scenarioNodeData"] });
    queryClient.resetQueries({ queryKey: ["scenarioNodes"] });
    queryClient.resetQueries({ queryKey: ["scenarioNodeMaterials"] });
    queryClient.resetQueries({ queryKey: ["scenarioNodeSupplementals"] });
    console.log("All caches cleared");
  }, [queryClient]);

  const hasLoggedInitRef = useRef(false);

  // Expose functions on window for console access
  useEffect(() => {
    window.testMultiModelData = {
      logStatus,
      refetchAll,
      clearIdbCache,
    };

    if (!hasLoggedInitRef.current) {
      // Log initial status
      console.log("Multi-Model Data Test initialized");
      console.log("Available commands:");
      console.log("  window.testMultiModelData.logStatus()");
      console.log("  window.testMultiModelData.refetchAll()");
      console.log("  window.testMultiModelData.clearIdbCache()");
      hasLoggedInitRef.current = true;
    }

    return () => {
      delete window.testMultiModelData;
    };
  }, [logStatus, refetchAll, clearIdbCache]);

  // Auto-log status when data changes
  useEffect(() => {
    if (scenarioNodeData.isSuccess) {
      logStatus();
    }
  }, [scenarioNodeData.isSuccess]);

  if (!showPanel) {
    return null;
  }

  const isLoading = scenarioNodeData.isLoading;
  const isFetching = scenarioNodeData.isFetching;

  // Helper to render status icon
  const StatusIcon = ({ loading, success }: { loading: boolean; success: boolean }) => {
    if (loading) return <HourglassIcon fontSize="small" color="warning" />;
    if (success) return <CheckCircleIcon fontSize="small" color="success" />;
    return <ErrorIcon fontSize="small" color="error" />;
  };

  return (
    <Dialog
      open={showPanel}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: "fixed",
          bottom: 16,
          right: 16,
          m: 0,
          maxHeight: "calc(100vh - 32px)",
          width: 480,
        },
      }}
      hideBackdrop
      disableEnforceFocus
      disableAutoFocus
      disableScrollLock
    >
      <Box sx={{ borderBottom: 1, borderColor: "divider", display: "flex", alignItems: "center" }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="debug panel tabs" sx={{ flexGrow: 1 }}>
          <Tab label="Multi-Model Data" {...a11yProps(0)} />
          <Tab label="Model Cache (OPFS)" {...a11yProps(1)} />
          <Tab label="Download Manager" {...a11yProps(2)} />
        </Tabs>
        {onClose && (
          <IconButton size="small" onClick={onClose} sx={{ mr: 1 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <DialogContent sx={{ p: 2 }}>
        {/* Tab 1: Multi-Model Data */}
        <TabPanel value={tabValue} index={0}>
          {/* Configuration Info */}
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
              <strong>Scenario:</strong> {scenarioId ?? "None"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Models:</strong> {selectedModelIds.length} selected
            </Typography>
          </Stack>

          {/* Status Chips */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip
              size="small"
              icon={isLoading ? <HourglassIcon /> : <CheckCircleIcon />}
              label={isLoading ? "Loading" : "Ready"}
              color={isLoading ? "warning" : "success"}
              variant="outlined"
            />
            {isFetching && <Chip size="small" icon={<RefreshIcon />} label="Fetching" color="info" variant="outlined" />}
          </Stack>

          {/* Data Table */}
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>Type</TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", py: 0.5 }}>
                  Status
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold", py: 0.5 }}>
                  Count
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ py: 0.5 }}>Nodes (supplementals)</TableCell>
                <TableCell align="center" sx={{ py: 0.5 }}>
                  <StatusIcon loading={scenarioNodeData.isLoading} success={scenarioNodeData.isSuccess} />
                </TableCell>
                <TableCell align="right" sx={{ py: 0.5 }}>
                  {Object.values(scenarioNodeData.nodes).reduce((sum: number, arr: any) => {
                    return sum + (arr?.length ?? 0);
                  }, 0)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ py: 0.5 }}>NodeSupplementals</TableCell>
                <TableCell align="center" sx={{ py: 0.5 }}>
                  <StatusIcon loading={scenarioNodeData.isLoading} success={scenarioNodeData.isSuccess} />
                </TableCell>
                <TableCell align="right" sx={{ py: 0.5 }}>
                  {Object.values(scenarioNodeData.nodeSupplementals).reduce((sum: number, arr: any) => {
                    return sum + (arr?.length ?? 0);
                  }, 0)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ py: 0.5 }}>NodeMaterials</TableCell>
                <TableCell align="center" sx={{ py: 0.5 }}>
                  <StatusIcon loading={scenarioNodeData.isLoading} success={scenarioNodeData.isSuccess} />
                </TableCell>
                <TableCell align="right" sx={{ py: 0.5 }}>
                  {Object.values(scenarioNodeData.nodeMaterials).reduce((sum: number, arr: any) => {
                    return sum + (arr?.length ?? 0);
                  }, 0)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <ButtonGroup size="small" variant="outlined">
              <Button onClick={logStatus} startIcon={<ListAltIcon />}>
                Log
              </Button>
              <Button onClick={clearIdbCache} startIcon={<DeleteIcon />}>
                Clear
              </Button>
              <Button onClick={refetchAll} disabled={isFetching} startIcon={<RefreshIcon />}>
                Refetch
              </Button>
            </ButtonGroup>
          </Box>
        </TabPanel>

        {/* Tab 2: Model Cache (OPFS) */}
        <TabPanel value={tabValue} index={1}>
          <ModelCachePanel />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <DownloadManagerPanel scenarioId={scenarioId} selectedModelIds={selectedModelIds} />
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Model Cache Panel - displays OPFS cached models and provides helper buttons
 */
function ModelCachePanel() {
  const [cachedModels, setCachedModels] = useState<CachedModelInfo[]>([]);
  const [stats, setStats] = useState<{ modelCount: number; totalSizeMB: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSupported, setIsSupported] = useState<boolean | null>(null);
  const [modelIdInput, setModelIdInput] = useState("");
  const [actionStatus, setActionStatus] = useState<string | null>(null);

  // Get ModelCache from window
  const getModelCache = (): ModelCacheDebug | null => {
    return (window as any).ModelCache ?? null;
  };

  // Load cached models list
  const loadCachedModels = useCallback(async () => {
    const cache = getModelCache();
    if (!cache) return;

    setIsLoading(true);
    setActionStatus(null);
    try {
      const models = await cache.list();
      const formatted: CachedModelInfo[] = models.map((m) => ({
        modelId: m.modelId,
        size: m.size,
        sizeMB: (m.size / 1024 / 1024).toFixed(2) + " MB",
        downloadedAt: m.metadata?.downloadedAt ? new Date(m.metadata.downloadedAt).toLocaleString() : null,
      }));
      setCachedModels(formatted);

      const statsResult = await cache.stats();
      setStats({
        modelCount: statsResult.modelCount,
        totalSizeMB: statsResult.totalSizeMB,
      });
    } catch (err) {
      console.error("Failed to load cached models:", err);
      setActionStatus("Error loading cache");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check if OPFS is supported
  useEffect(() => {
    const cache = getModelCache();
    if (cache) {
      setIsSupported(cache.isSupported());
      loadCachedModels();
    } else {
      setIsSupported(false);
    }
  }, [loadCachedModels]);

  // Clear all caches
  const handleClearAll = async () => {
    const cache = getModelCache();
    if (!cache) return;

    setIsLoading(true);
    setActionStatus("Clearing all caches...");
    try {
      await cache.clearAll();
      setActionStatus("All caches cleared");
      await loadCachedModels();
    } catch (err) {
      console.error("Failed to clear caches:", err);
      setActionStatus("Error clearing caches");
    } finally {
      setIsLoading(false);
    }
  };

  // Clear specific model
  const handleClearModel = async (modelId: string) => {
    const cache = getModelCache();
    if (!cache) return;

    setIsLoading(true);
    setActionStatus(`Clearing ${modelId}...`);
    try {
      await cache.clear(modelId);
      setActionStatus(`Cleared ${modelId}`);
      await loadCachedModels();
    } catch (err) {
      console.error("Failed to clear model cache:", err);
      setActionStatus("Error clearing model");
    } finally {
      setIsLoading(false);
    }
  };

  // Check model status
  const handleCheckStatus = async () => {
    if (!modelIdInput.trim()) return;

    const cache = getModelCache();
    if (!cache) return;

    setIsLoading(true);
    setActionStatus("Checking status...");
    try {
      const status = await cache.status(modelIdInput.trim());
      console.log("Model status:", status);
      setActionStatus(status.cached ? `Cached: ${((status.fileSize || 0) / 1024 / 1024).toFixed(2)} MB` : "Not cached");
    } catch (err) {
      console.error("Failed to check status:", err);
      setActionStatus("Error checking status");
    } finally {
      setIsLoading(false);
    }
  };

  // Force redownload model
  const handleRedownload = async () => {
    if (!modelIdInput.trim()) return;

    const cache = getModelCache();
    if (!cache) return;

    setIsLoading(true);
    setActionStatus("Redownloading...");
    try {
      const result = await cache.redownload(modelIdInput.trim());
      console.log("Redownload result:", result);
      setActionStatus(result.success ? "Redownload complete" : `Failed: ${result.error}`);
      await loadCachedModels();
    } catch (err) {
      console.error("Failed to redownload:", err);
      setActionStatus("Error redownloading");
    } finally {
      setIsLoading(false);
    }
  };

  // Download with parallel API
  const handleDownloadParallel = async () => {
    if (!modelIdInput.trim()) return;

    const cache = getModelCache();
    if (!cache) return;

    setIsLoading(true);
    setActionStatus("Downloading (parallel)...");
    try {
      const result = await cache.downloadParallel(modelIdInput.trim());
      console.log("Download result:", result);
      setActionStatus(result.success ? "Download complete" : `Failed: ${result.error}`);
      await loadCachedModels();
    } catch (err) {
      console.error("Failed to download:", err);
      setActionStatus("Error downloading");
    } finally {
      setIsLoading(false);
    }
  };

  if (isSupported === false) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <ErrorIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
        <Typography color="error">OPFS is not supported in this browser or ModelCache is not initialized.</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {/* Header with stats */}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <StorageIcon color="primary" />
            <Typography variant="subtitle2" fontWeight="bold">
              OPFS Model Cache
            </Typography>
          </Stack>
          {stats && <Chip size="small" label={`${stats.modelCount} models | ${stats.totalSizeMB}`} variant="outlined" color="info" />}
        </Stack>
      </Paper>

      {/* Action status */}
      {actionStatus && (
        <Paper
          variant="outlined"
          sx={{
            p: 1,
            bgcolor: actionStatus.startsWith("Error") ? "error.dark" : "success.dark",
            color: "white",
          }}
        >
          <Typography variant="body2">{actionStatus}</Typography>
        </Paper>
      )}

      {/* Model ID input + actions */}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
          Model ID Actions
        </Typography>
        <Stack spacing={1}>
          <TextField size="small" fullWidth placeholder="Enter Model ID (GUID)" value={modelIdInput} onChange={(e) => setModelIdInput(e.target.value)} disabled={isLoading} />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Tooltip title="Check if model is cached">
              <Button size="small" variant="outlined" startIcon={<InfoIcon />} onClick={handleCheckStatus} disabled={isLoading || !modelIdInput.trim()}>
                Status
              </Button>
            </Tooltip>
            <Tooltip title="Force re-download model">
              <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={handleRedownload} disabled={isLoading || !modelIdInput.trim()}>
                Redownload
              </Button>
            </Tooltip>
            <Tooltip title="Download with parallel API">
              <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadParallel} disabled={isLoading || !modelIdInput.trim()}>
                Download
              </Button>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* Cached models list */}
      <Divider />
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" fontWeight="bold">
          Cached Models
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh list">
            <IconButton size="small" onClick={loadCachedModels} disabled={isLoading}>
              {isLoading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear all caches">
            <Button size="small" variant="outlined" startIcon={<DeleteIcon />} onClick={handleClearAll} disabled={isLoading || cachedModels.length === 0}>
              Clear All
            </Button>
          </Tooltip>
        </Stack>
      </Stack>

      {cachedModels.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <Typography color="text.secondary">No models cached</Typography>
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>Model ID</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold", py: 0.5 }}>
                Size
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold", py: 0.5 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cachedModels.map((model) => (
              <TableRow key={model.modelId} hover>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", cursor: "pointer", wordBreak: "break-all", fontSize: "0.75rem" }}
                    onClick={() => setModelIdInput(model.modelId)}
                  >
                    {model.modelId}
                  </Typography>
                  {model.downloadedAt && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {model.downloadedAt}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ py: 0.5 }}>
                  <Typography variant="body2">{model.sizeMB}</Typography>
                </TableCell>
                <TableCell align="center" sx={{ py: 0.5 }}>
                  <Tooltip title="Clear this model">
                    <IconButton size="small" onClick={() => handleClearModel(model.modelId)} disabled={isLoading}>
                      <ClearOneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Stack>
  );
}

export default MultiModelDataTest;

/**
 * Download Manager Panel - enqueue/cancel downloads and view progress
 */
function DownloadManagerPanel({ scenarioId, selectedModelIds }: { scenarioId: string | undefined; selectedModelIds: string[] }) {
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();
  const [concurrency, setConcurrency] = useState<number>(4);
  const [priority, setPriority] = useState<"high" | "medium" | "low">("medium");
  const [manualModelId, setManualModelId] = useState<string>("");

  const state = appStore.downloadManager?.state;
  const perModelEntries = Object.entries(state?.perModel || {});

  const enqueueSelected = () => {
    if (selectedModelIds.length === 0) return;
    appStore.downloadManager.enqueueLoad(selectedModelIds, priority);
  };

  const cancelSelected = () => {
    if (selectedModelIds.length === 0) return;
    appStore.downloadManager.cancel(selectedModelIds);
  };

  const enqueueAllProjectModels = () => {
    const ids = (selectionManager.selectedProject?.models || []).map((m: any) => m.modelId).filter(Boolean);
    if (ids.length > 0) appStore.downloadManager.enqueueLoad(ids, priority);
  };

  const cancelAllActive = () => {
    const ids = perModelEntries.map(([id]) => id);
    if (ids.length > 0) appStore.downloadManager.cancel(ids);
  };

  const enqueueManual = () => {
    const id = manualModelId.trim();
    if (id) appStore.downloadManager.enqueueLoad(id, priority);
  };

  const cancelManual = () => {
    const id = manualModelId.trim();
    if (id) appStore.downloadManager.cancel(id);
  };

  const attachDebug = () => {
    appStore.downloadManager.attachDebug();
  };

  const setThreads = () => {
    appStore.downloadManager.setWorkerThreadLimit(concurrency);
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack spacing={0.5}>
            <Typography variant="subtitle2" fontWeight="bold">
              Download Manager
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Scenario: {scenarioId ?? "None"} | Selected Models: {selectedModelIds.length}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Chip size="small" color="info" variant="outlined" label={`Total ${state?.total ?? 0}`} />
            <Chip size="small" color="warning" variant="outlined" label={`In-Progress ${state?.inProgress ?? 0}`} />
            <Chip size="small" color="success" variant="outlined" label={`Completed ${state?.completed ?? 0}`} />
          </Stack>
        </Stack>
      </Paper>

      {/* Selected models list (name + GUID) */}
      {selectedModelIds.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            Selected Models (click GUID to use below)
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>Model ID (GUID)</TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", py: 0.5 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedModelIds.map((id) => {
                const name = appStore.getModelDisplayName(id);
                return (
                  <TableRow key={id}>
                    <TableCell sx={{ py: 0.5 }}>{name}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", wordBreak: "break-all", fontSize: "0.75rem", cursor: "pointer" }}
                        onClick={() => setManualModelId(id)}
                        title="Click to use this ID in the input below"
                      >
                        {id}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.5 }}>
                      <Button size="small" variant="outlined" onClick={() => setManualModelId(id)}>
                        Use
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="caption" color="text.secondary">
              Priority
            </Typography>
            <ButtonGroup size="small" variant="outlined">
              <Button onClick={() => setPriority("high")} color={priority === "high" ? "primary" : "inherit"}>
                High
              </Button>
              <Button onClick={() => setPriority("medium")} color={priority === "medium" ? "primary" : "inherit"}>
                Medium
              </Button>
              <Button onClick={() => setPriority("low")} color={priority === "low" ? "primary" : "inherit"}>
                Low
              </Button>
            </ButtonGroup>
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Concurrency
            </Typography>
            <TextField size="small" sx={{ width: 80 }} type="number" value={concurrency} onChange={(e) => setConcurrency(Math.max(1, Number(e.target.value) || 1))} />
            <Button size="small" variant="outlined" onClick={setThreads}>
              Set
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} disabled={selectedModelIds.length === 0} onClick={enqueueSelected}>
              Enqueue Selected
            </Button>
            <Button size="small" variant="outlined" startIcon={<DeleteIcon />} disabled={selectedModelIds.length === 0} onClick={cancelSelected}>
              Cancel Selected
            </Button>
            <Button size="small" variant="outlined" onClick={enqueueAllProjectModels}>
              Enqueue All Project Models
            </Button>
            <Button size="small" variant="outlined" onClick={cancelAllActive} disabled={perModelEntries.length === 0}>
              Cancel All Active
            </Button>
            <Button size="small" variant="outlined" onClick={attachDebug}>
              Attach Debug
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <TextField size="small" fullWidth placeholder="Enter Model ID (GUID)" value={manualModelId} onChange={(e) => setManualModelId(e.target.value)} />
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={enqueueManual} disabled={!manualModelId.trim()}>
              Enqueue
            </Button>
            <Button size="small" variant="outlined" startIcon={<ClearOneIcon />} onClick={cancelManual} disabled={!manualModelId.trim()}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Per Model Status
          </Typography>
          <Tooltip title="Refresh in console via window.downloadManager.state()">
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        {perModelEntries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No active downloads
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>Model ID</TableCell>
                <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>Message</TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", py: 0.5 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {perModelEntries.map(([modelId, s]) => (
                <TableRow key={modelId}>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all", fontSize: "0.75rem" }}>
                      {modelId}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>{(s as any).status}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    {(() => {
                      const st: any = s;
                      if (st?.message) return st.message;
                      const phase: string | undefined = st?.phase;
                      const percent: number | undefined = st?.percent;
                      if (!phase) return "";
                      if (phase === "downloading" && typeof percent === "number") {
                        return `Downloading ${percent}%`;
                      }
                      return String(phase).charAt(0).toUpperCase() + String(phase).slice(1);
                    })()}
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.5 }}>
                    <Tooltip title="Cancel">
                      <IconButton size="small" onClick={() => appStore.downloadManager.cancel(modelId)}>
                        <ClearOneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}
